/**
 * @memberof module:util
 * @file quoteBookingForm — maps a presented-RFQ offer card (built by
 * {@link module:util.buildOfferRateJson}) plus the orchestrator's Quote record
 * into the `final_search_form` shape that FInalView (the booking-confirmation
 * screen) renders + books from.
 *
 * Why a separate mapper: the normal flow builds final_search_form in
 * MainResultCard.sendUserData from a LIVE rate card the user clicked. An RFQ
 * quote has no live card session, so the portal's "Accept & create booking"
 * button hands off here — we rebuild the same shape from the agreed quote so the
 * customer lands on the real booking page with everything pre-filled (Neil #188).
 *
 * Offer cards fold the WHOLE agreed sell into the freight line and zero the
 * view-detail sub-sections (see rfqOfferCards), so here only FREIGHT is "checked"
 * and the card total === the quote sell. The offer card overlays the carrier NAME
 * only; we overlay the Quote record's real carrier / vessel / discharge-port /
 * validity IDs so the created Booking references the actual selected quote, not
 * the cloned sample template. Pure + per-mode → unit-tested against live FCL/LCL/
 * AIR fixtures.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// FInalView spins forever on a truthy schedule with no id, and an RFQ quote has no
// sailing schedule yet (phase-2) — null keeps the schedule section (and its
// spinner) hidden while the rest of the page renders.
const NO_SCHEDULE = null;

// selected_card_json: carry the QUOTE's real carrier / vessel / discharge port /
// validity (the offer card overlays only the display name), falling back to the
// cloned-template values so a missing id never crashes the booking payload. The
// freight line already holds the whole sell, so freight-only sections are emptied.
const buildSelectedCardJson = (card, record, rfqOffer) => ({
  ...card,
  carrier: { id: record.carrier?.id ?? card.carrier?.id ?? null, companyName: record.carrier?.companyName ?? card.sealine ?? '' },
  vessel_Service: { id: record.vesselService?.id ?? card.vessel_Service?.id ?? null, name: record.vesselService?.name ?? card.vessel_Service?.name ?? '' },
  port_of_discharge: {
    id: record.locationTo?.id ?? card.port_of_discharge?.id ?? null,
    company_name: record.locationTo?.companyName ?? card.port_of_discharge?.company_name ?? '',
  },
  vendor_contract: card.vendor_contract || { id: null },
  // Validity reflects the QUOTE only — never inherit the cloned template's stale
  // placeholder window (a booking must not carry a long-expired validity). Null
  // when the quote has none; a properly-assembled quote sets it.
  valid_date_from: record.validityFrom ?? null,
  valid_date_to: record.validityTo ?? null,
  etd_origin: card.etd_origin ?? record.etdOrigin ?? null,
  mode: record.mode === 'Air' ? 'AIR' : (card.mode || 'OCEAN'),
  spotRate: false,
  isOverweight: false,
  freightCharges: [],
  pickup_section: [],
  delivery_section: [],
  rfqOffer: { ...rfqOffer, rfqId: rfqOffer.rfqId ?? record.rfq ?? null },
});

// selected_card_detail: freight carries the whole sell; every other section is
// folded in (unchecked, empty charge arrays so FInalView's .map never throws).
const buildSelectedCardDetail = (card, sell) => ({
  bookNowPrice: sell,
  pickup_isChecked: false,
  origin_isChecked: false,
  frieght_isChecked: true,
  discharge_isChecked: false,
  ers_isChecked: false,
  delivery_isChecked: false,
  id: card.id,
  selectedSchedule: NO_SCHEDULE,
  pickup_selected_option_index: 0,
  ers_selected_option_index: 0,
  delivery_selected_option_index: 0,
  pickup_charges: [],
  ers_charges: [],
  delivery_charges: [],
  port_fees_from_charges: [],
  port_fees_to_charges: [],
  show_variable_rate: false,
});

/**
 * @param {Object} args
 * @param {Object} args.card    - one offer card from buildOfferRateJson (carries rfqOffer + per-size price / rate).
 * @param {Object} args.record  - GET /quote/:id/record (carrier/vesselService/locationTo/validity/totalPrice/mode).
 * @param {'fcl'|'lcl'|'air'} args.mode
 * @param {Object} [args.counters]          - FCL container counters (seeded fclCounters).
 * @param {Object} [args.shipment]          - LCL/Air {totalVolume,totalWeight,weightUnit,volumeUnit}.
 * @param {number} [args.chargeableWeight]  - LCL chargeable weight (drives freight_rate × cw = sell).
 * @returns {Object} final_search_form (the save_final_form payload)
 */
export const offerCardToFinalSearchForm = ({ card = {}, record = {}, mode, counters = {}, shipment, chargeableWeight }) => {
  const rfqOffer = card.rfqOffer || {};
  const sell = num(rfqOffer.sell) || num(record.totalPrice);
  const selected_card_json = buildSelectedCardJson(card, record, rfqOffer);
  const selected_card_detail = buildSelectedCardDetail(card, sell);

  const form = {
    type: `api-${mode}`,
    selected_card_json,
    selected_card_detail,
    origin: { selectedPort: card.port_of_origin },
  };

  if (mode === 'fcl') {
    form.container = {
      _20stCounter: num(counters._20stCounter),
      _40stCounter: num(counters._40stCounter),
      _40hqCounter: num(counters._40hqCounter),
      _45hqCounter: num(counters._45hqCounter),
    };
    // The offer card folded the whole freight into each size's per-container price
    // (price × counter === sell). Mirror onto json (no-discount booking branch) +
    // detail (discount branch) so handleCreateBooking reads the right number either way.
    selected_card_json['20st'] = num(card['20st']);
    selected_card_json['40st'] = num(card['40st']);
    selected_card_json['40hq'] = num(card['40hq']);
    selected_card_json['45hq'] = num(card['45hq']);
    selected_card_detail._20stPrice = num(card['20st']);
    selected_card_detail._40stPrice = num(card['40st']);
    selected_card_detail._40hqPrice = num(card['40hq']);
    selected_card_detail._45hqPrice = num(card['45hq']);
  } else {
    form.total_shipment = shipment || { totalVolume: 0, totalWeight: 0, weightUnit: 'KG', volumeUnit: 'CBM' };
    if (mode === 'lcl') {
      form.chargeableWeight = num(chargeableWeight);
      form.freightRate = num(card.rate);
    }
  }
  return form;
};
